import { useState, useEffect } from 'react';
import { getAuthToken } from '../../utils/cookieUtils';
import {
  Paper,
  Typography,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel
} from '@mui/material';
import {
  Refresh
} from '@mui/icons-material';
import { 
  saveFacilityToStorage, 
  loadFacilityFromStorage, 
  formatFacilityOptions
} from '../../utils/facilityStorage';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Interface for facility
interface Facility {
  id: number;
  name: string;
}

// Types for drivers
interface Driver {
  id: string;
  name: string;
  trailerNumber: string;
  scac: string;
  type: string;
  arrivalTime: string;
  arrivalTimeSort: Date;
  shipmentNumber: string;
  trailerStatus?: string;
  locationName?: string;
  locationType?: string;
  lastKnownLocationName?: string;
  lastKnownLocationType?: string;
  shipmentType?: string;
  checkInTime?: string;
  checkInTimeSort?: Date;
  mdtTime?: string;
  mdtTimeSort?: Date;
}

type ViewMode = 'unassigned' | 'assigned' | 'recentlyExited';
type SortField = 'name' | 'trailerNumber' | 'scac' | 'type' | 'arrivalTime' | 'shipmentNumber' | 'trailerStatus' | 'locationName' | 'lastKnownLocationName' | 'shipmentType' | 'checkInTime' | 'mdtTime';
type SortDirection = 'asc' | 'desc';

const WhiteBoard = () => {
  // State for facilities
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };
  
  // State for time period filter
  const [sinceHours, setSinceHours] = useState<number>(48);
  
  // State
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('unassigned');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Time period options - conditional based on view mode
  const getTimeOptions = () => {
    if (viewMode === 'recentlyExited') {
      return [
        { value: 1.5, label: '90 minutes' },
        { value: 24, label: '24 hours' },
      ];
    }
    return [
      { value: 24, label: '24 hours' },
      { value: 48, label: '48 hours' },
      { value: 168, label: '1 week' }
    ];
  };

  // Get default time value based on view mode
  const getDefaultTimeValue = (mode: ViewMode) => {
    return mode === 'recentlyExited' ? 1.5 : 48;
  };

  // Fetch facilities using GraphQL - same as DriverSlotAssignment
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, set default
            if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
        setError('Failed to fetch facilities');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFacilities();
  }, []);

  // Fetch drivers using GraphQL - using the same query for both modes
  const fetchDriversData = async () => {
    if (!selectedFacility) return;
    
    try {
      setIsLoadingData(true);
      setError(null);

      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query - different queries based on view mode
      const queryMethod = viewMode === 'assigned' ? 'assignedDriverAssignments' :
                         viewMode === 'recentlyExited' ? 'recentlyExitedDriverAssignments' :
                         'unassignedDriverAssignments';

      // For recently exited, the trailer is no longer active on the shipment,
      // so we query the trailer at the root level of the driver assignment instead.
      const isRecentlyExited = viewMode === 'recentlyExited';

      const trailerFragment = `
        uuid
        trailerNumber
        status {
          name
        }
        currentLocation {
          ... on YardSpotObject {
            __typename
            uuid
            name
          }
          ... on DockObject {
            __typename
            uuid
            name
          }
          ... on FacilityZoneObject {
            __typename
            uuid
            name
          }
        }
        lastKnownLocation {
          ... on YardSpotObject {
            __typename
            uuid
            name
          }
          ... on DockObject {
            __typename
            uuid
            name
          }
          ... on FacilityZoneObject {
            __typename
            uuid
            name
          }
        }
        carrier {
          name
        }
      `;

      const driversQuery = `
        query GetDrivers {
          ${queryMethod}(facilityId: "${selectedFacility.id}", sinceHours: ${sinceHours}) {
            success
            message
            totalCount
            driverAssignments {
              id
              shipmentId
              userId
              driverId
              phone
              assignedAt
              revokedAt
              checkIn
              checkOut
              status
              archived
              archivedAt
              createdAt
              updatedAt

              driver {
                id
                firstName
                lastName
              }

              ${isRecentlyExited ? `
              trailer {
                ${trailerFragment}
              }
              ` : ''}

              shipment {
                id
                referenceNumber
                direction
                metadata
                ${!isRecentlyExited ? `
                trailer {
                  ${trailerFragment}
                }
                ` : ''}
              }
            }
          }
        }
      `;

      // Execute the GraphQL query
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: driversQuery
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Transform drivers data
      // For recently exited, trailer data comes from the root-level assignment.trailer
      // since the trailer is no longer active on the shipment.
      const transformedDrivers: Driver[] = result.data[queryMethod].driverAssignments.map((assignment: any) => {
        const trailer = isRecentlyExited ? assignment.trailer : assignment.shipment?.trailer;
        const appointmentDate = assignment.shipment?.metadata?.appointment_date
          ? new Date(assignment.shipment.metadata.appointment_date)
          : null;
        const checkInDate = assignment.createdAt ? new Date(assignment.createdAt) : null;
        const mdtDate = assignment.shipment?.metadata?.appointment_must_depart_by_date
          ? new Date(assignment.shipment.metadata.appointment_must_depart_by_date)
          : null;

        return {
          id: assignment.id,
          name: assignment.driver
            ? `${assignment.driver.firstName} ${assignment.driver.lastName}`.trim()
            : 'Unknown Driver',
          trailerNumber: trailer?.trailerNumber || '',
          scac: trailer?.carrier?.name || '',
          type: assignment.shipment?.direction || '',
          arrivalTime: appointmentDate ? appointmentDate.toLocaleString() : '',
          arrivalTimeSort: appointmentDate || new Date(0),
          shipmentNumber: assignment.shipment?.referenceNumber || assignment.shipmentId,
          direction: assignment.shipment?.direction || '',
          trailerStatus: trailer?.status?.name || '',
          locationName: trailer?.currentLocation?.name || '',
          locationType: trailer?.currentLocation?.__typename || '',
          lastKnownLocationName: trailer?.lastKnownLocation?.name || '',
          lastKnownLocationType: trailer?.lastKnownLocation?.__typename || '',
          shipmentType: assignment.shipment?.metadata?.shipment_type || '',
          checkInTime: checkInDate ? checkInDate.toLocaleString() : '',
          checkInTimeSort: checkInDate || undefined,
          mdtTime: mdtDate ? mdtDate.toLocaleString() : '',
          mdtTimeSort: mdtDate || undefined
        };
      });

      setDrivers(transformedDrivers);

    } catch (err) {
      console.error('Failed to fetch drivers:', err);
      setError('Failed to fetch drivers data');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch data when facility or view mode changes
  useEffect(() => {
    if (selectedFacility) {
      fetchDriversData();
    }
  }, [selectedFacility, viewMode, sinceHours]);
  
  // Handle facility change
  const handleFacilityChange = (event: any) => {
    const facilityId = event.target.value;
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setSelectedFacility(facility);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (event: any) => {
    const newViewMode = event.target.value as ViewMode;
    setViewMode(newViewMode);
    // Set appropriate default time value for the new view mode
    setSinceHours(getDefaultTimeValue(newViewMode));
  };

  // Handle refresh
  const handleRefresh = () => {
    if (selectedFacility) {
      fetchDriversData();
    }
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  // Sort drivers
  const sortedDrivers = [...drivers].sort((a, b) => {
    // Special handling for date fields to sort by actual date
    if (sortField === 'arrivalTime') {
      const aDate = a.arrivalTimeSort;
      const bDate = b.arrivalTimeSort;
      
      if (sortDirection === 'asc') {
        return aDate.getTime() - bDate.getTime();
      } else {
        return bDate.getTime() - aDate.getTime();
      }
    }
    
    if (sortField === 'checkInTime') {
      const aDate = a.checkInTimeSort;
      const bDate = b.checkInTimeSort;
      
      if (!aDate && !bDate) return 0;
      if (!aDate) return sortDirection === 'asc' ? 1 : -1;
      if (!bDate) return sortDirection === 'asc' ? -1 : 1;
      
      if (sortDirection === 'asc') {
        return aDate.getTime() - bDate.getTime();
      } else {
        return bDate.getTime() - aDate.getTime();
      }
    }
    
    if (sortField === 'mdtTime') {
      const aDate = a.mdtTimeSort;
      const bDate = b.mdtTimeSort;
      
      if (!aDate && !bDate) return 0;
      if (!aDate) return sortDirection === 'asc' ? 1 : -1;
      if (!bDate) return sortDirection === 'asc' ? -1 : 1;
      
      if (sortDirection === 'asc') {
        return aDate.getTime() - bDate.getTime();
      } else {
        return bDate.getTime() - aDate.getTime();
      }
    }
    
    // String comparison for other fields
    const aValue = a[sortField] || '';
    const bValue = b[sortField] || '';
    
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  // Get driver type color
  const getDriverTypeColor = (type: string): "default" | "success" | "primary" | "error" | "secondary" | "info" | "warning" | undefined => {
    switch (type) {
      case 'inbound':
        return 'success';
      case 'outbound':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Loading state for initial facilities fetch
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading facilities...</Typography>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error}</Typography>
        <IconButton onClick={handleRefresh}>
          <Refresh />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          Driver Board
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Facility Selector */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Facility</InputLabel>
            <Select
              value={selectedFacility?.id || ''}
              onChange={handleFacilityChange}
              label="Facility"
            >
              {facilities.map(facility => (
                <MenuItem key={facility.id} value={facility.id}>
                  {facility.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* View Mode Selector */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>View</InputLabel>
            <Select
              value={viewMode}
              onChange={handleViewModeChange}
              label="View"
            >
              <MenuItem value="unassigned">Unassigned Drivers</MenuItem>
              <MenuItem value="assigned">Assigned Drivers</MenuItem>
              <MenuItem value="recentlyExited">Recently Exited</MenuItem>
            </Select>
          </FormControl>

          {/* Time Period Selector */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={sinceHours}
              onChange={(event) => setSinceHours(event.target.value as number)}
              label="Time Period"
            >
              {getTimeOptions().map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Refresh Button */}
          <IconButton 
            onClick={handleRefresh} 
            disabled={isLoadingData}
            color="primary"
          >
            {isLoadingData ? <CircularProgress size={24} /> : <Refresh />}
          </IconButton>

          {/* Driver Count */}
          <Chip 
            label={`${drivers.length} drivers`} 
            color="primary" 
            size="small" 
          />
        </Box>
      </Box>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Driver Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'shipmentNumber'}
                    direction={sortField === 'shipmentNumber' ? sortDirection : 'asc'}
                    onClick={() => handleSort('shipmentNumber')}
                  >
                    Shipment
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'trailerNumber'}
                    direction={sortField === 'trailerNumber' ? sortDirection : 'asc'}
                    onClick={() => handleSort('trailerNumber')}
                  >
                    Trailer Number
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'scac'}
                    direction={sortField === 'scac' ? sortDirection : 'asc'}
                    onClick={() => handleSort('scac')}
                  >
                    SCAC
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'type'}
                    direction={sortField === 'type' ? sortDirection : 'asc'}
                    onClick={() => handleSort('type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'shipmentType'}
                    direction={sortField === 'shipmentType' ? sortDirection : 'asc'}
                    onClick={() => handleSort('shipmentType')}
                  >
                    Shipment Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'trailerStatus'}
                    direction={sortField === 'trailerStatus' ? sortDirection : 'asc'}
                    onClick={() => handleSort('trailerStatus')}
                  >
                    Trailer Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'arrivalTime'}
                    direction={sortField === 'arrivalTime' ? sortDirection : 'asc'}
                    onClick={() => handleSort('arrivalTime')}
                  >
                    Appointment Time
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'checkInTime'}
                    direction={sortField === 'checkInTime' ? sortDirection : 'asc'}
                    onClick={() => handleSort('checkInTime')}
                  >
                    Check-in Time
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'mdtTime'}
                    direction={sortField === 'mdtTime' ? sortDirection : 'asc'}
                    onClick={() => handleSort('mdtTime')}
                  >
                    MDT Time
                  </TableSortLabel>
                </TableCell>
                {viewMode === 'assigned' && (
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'locationName'}
                      direction={sortField === 'locationName' ? sortDirection : 'asc'}
                      onClick={() => handleSort('locationName')}
                    >
                      Current Location
                    </TableSortLabel>
                  </TableCell>
                )}
                {viewMode === 'recentlyExited' && (
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'lastKnownLocationName'}
                      direction={sortField === 'lastKnownLocationName' ? sortDirection : 'asc'}
                      onClick={() => handleSort('lastKnownLocationName')}
                    >
                      Last Known Location
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingData ? (
                <TableRow>
                  <TableCell colSpan={viewMode === 'assigned' || viewMode === 'recentlyExited' ? 12 : 11} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sortedDrivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={viewMode === 'assigned' || viewMode === 'recentlyExited' ? 12 : 11} align="center" sx={{ py: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      No {viewMode === 'recentlyExited' ? 'recently exited' : viewMode} drivers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedDrivers.map(driver => (
                  <TableRow key={driver.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {driver.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{driver.shipmentNumber}</TableCell>
                    <TableCell>{driver.trailerNumber || '-'}</TableCell>
                    <TableCell>{driver.scac || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={driver.type.charAt(0).toUpperCase() + driver.type.slice(1)} 
                        color={getDriverTypeColor(driver.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {driver.shipmentType ? (
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {driver.shipmentType === 'L' ? 'Live' : driver.shipmentType === 'D' ? 'Drop' : driver.shipmentType}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {driver.trailerStatus || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {driver.arrivalTime ? (
                        <Typography variant="body2">
                          {driver.arrivalTime}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {driver.checkInTime ? (
                        <Typography variant="body2">
                          {driver.checkInTime}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {driver.mdtTime ? (
                        <Typography variant="body2">
                          {driver.mdtTime}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    {viewMode === 'assigned' && (
                      <TableCell>
                        {driver.locationName ? (
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {driver.locationName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {driver.locationType?.replace('Object', '')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No location
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {viewMode === 'recentlyExited' && (
                      <TableCell>
                        {driver.lastKnownLocationName ? (
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {driver.lastKnownLocationName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {driver.lastKnownLocationType?.replace('Object', '')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No location
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip 
                        label={viewMode === 'assigned' ? 'Assigned' : 
                               viewMode === 'recentlyExited' ? 'Exited' : 'Waiting'}
                        color={viewMode === 'assigned' ? 'success' : 
                               viewMode === 'recentlyExited' ? 'info' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default WhiteBoard; 